import { memo, useState, useEffect, useCallback } from "react";
import MoonLoader from "react-spinners/MoonLoader";
import { ColorPicker, ContactLabel } from "../../../../jsx/components/ContactsBoard/Label";
import { getRandomColor } from "../../../../jsx/components/ContactsBoard/label-utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TEMPERATURES = [
  { value: "", label: "Sem temperatura" },
  { value: "cold", label: "Frio" },
  { value: "warm", label: "Morno" },
  { value: "hot", label: "Quente" },
];

const CRMCreateForm = memo(({
  name,
  phone,
  email,
  pipelines,
  columns,
  isCreating,
  onLoadColumns,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: name || "",
    phone: phone || "",
    email: email || "",
    pipeline: "",
    column: "",
    value: "",
    priority: "",
    temperature: "",
  });
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState(getRandomColor());
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  // Atualizar dados quando props mudam
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: name || prev.name,
      phone: phone || prev.phone,
      email: email || prev.email,
    }));
  }, [name, phone, email]);

  // Auto-selecionar primeiro pipeline
  useEffect(() => {
    if (pipelines.length > 0 && !formData.pipeline) {
      const firstId = pipelines[0].id;
      setFormData((prev) => ({ ...prev, pipeline: String(firstId) }));
      onLoadColumns(firstId);
    }
  }, [pipelines, formData.pipeline, onLoadColumns]);

  // Auto-selecionar primeira coluna
  useEffect(() => {
    if (columns.length > 0 && !formData.column) {
      setFormData((prev) => ({ ...prev, column: String(columns[0].id) }));
    }
  }, [columns, formData.column]);

  // Validação de email
  const validateEmail = useCallback((value) => {
    if (!value.trim()) {
      setEmailError("");
      return true;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError("E-mail inválido");
      return false;
    }
    setEmailError("");
    return true;
  }, []);

  const handleChange = useCallback((e) => {
    const { name: field, value } = e.target;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "email") {
      if (emailTouched) validateEmail(value);
    }

    if (field === "pipeline") {
      setFormData((prev) => ({ ...prev, pipeline: value, column: "" }));
      onLoadColumns(value);
    }
  }, [onLoadColumns, emailTouched, validateEmail]);

  const handleEmailBlur = useCallback(() => {
    setEmailTouched(true);
    validateEmail(formData.email);
  }, [formData.email, validateEmail]);

  const formatPhoneForAPI = (p) => {
    if (!p) return "";
    const digits = p.replace(/\D/g, "");
    if (digits && !digits.startsWith("55")) return `+55${digits}`;
    return `+${digits}`;
  };

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.pipeline || !formData.column) return;

    // Validar email antes de enviar
    if (formData.email.trim() && !EMAIL_REGEX.test(formData.email)) {
      setEmailTouched(true);
      setEmailError("E-mail inválido");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      phone: formatPhoneForAPI(formData.phone),
      email: formData.email.trim() || undefined,
      pipeline: Number(formData.pipeline),
      column: Number(formData.column),
    };

    if (formData.value) payload.value = parseFloat(formData.value) || 0;
    if (formData.priority) payload.priority = formData.priority;
    if (formData.temperature) payload.temperature = formData.temperature;
    if (labelName.trim()) payload.label = { name: labelName.trim(), color: labelColor };

    onSubmit(payload);
  }, [formData, labelName, labelColor, onSubmit]);

  const isValid = formData.name.trim() && formData.pipeline && formData.column && !emailError;

  return (
    <div className="sc-crm-create">
      <div className="sc-crm-create__header">
        <div className="sc-crm-create__header-icon">📋</div>
        <h4 className="sc-crm-create__title">Este contato ainda não está no CRM</h4>
        <p className="sc-crm-create__subtitle">Adicione para acompanhar negociações</p>
      </div>

      <form className="sc-crm-create__form" onSubmit={handleSubmit}>
        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">Nome *</label>
          <input
            className="sc-crm-create__input"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Nome do contato"
          />
        </div>

        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">Telefone</label>
          <input
            className="sc-crm-create__input sc-crm-create__input--readonly"
            type="text"
            value={formData.phone}
            readOnly
          />
        </div>

        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">E-mail</label>
          <input
            className={`sc-crm-create__input ${emailError ? "sc-crm-create__input--error" : ""}`}
            type="text"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
            placeholder="email@exemplo.com"
          />
          {emailError && (
            <span className="sc-crm-create__field-error">{emailError}</span>
          )}
        </div>

        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">Pipeline *</label>
          <select
            className="sc-crm-create__select"
            name="pipeline"
            value={formData.pipeline}
            onChange={handleChange}
          >
            <option value="">Selecione...</option>
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">Coluna *</label>
          <select
            className="sc-crm-create__select"
            name="column"
            value={formData.column}
            onChange={handleChange}
            disabled={!formData.pipeline}
          >
            <option value="">Selecione...</option>
            {columns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="sc-crm-create__row">
          <div className="sc-crm-create__field">
            <label className="sc-crm-create__label">Valor</label>
            <input
              className="sc-crm-create__input"
              type="number"
              name="value"
              value={formData.value}
              onChange={handleChange}
              placeholder="R$ 0,00"
              min="0"
              step="0.01"
            />
          </div>
          <div className="sc-crm-create__field">
            <label className="sc-crm-create__label">Prioridade</label>
            <select
              className="sc-crm-create__select"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
            >
              <option value="">Nenhuma</option>
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
            </select>
          </div>
        </div>

        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">Temperatura</label>
          <select
            className="sc-crm-create__select"
            name="temperature"
            value={formData.temperature}
            onChange={handleChange}
          >
            {TEMPERATURES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="sc-crm-create__field">
          <label className="sc-crm-create__label">Etiqueta</label>
          <div className="sc-crm-create__label-row">
            <input
              className="sc-crm-create__input"
              type="text"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              placeholder="Nome da etiqueta"
            />
            {labelName && (
              <button
                type="button"
                className="sc-crm-create__label-remove"
                onClick={() => setLabelName("")}
              >
                Remover
              </button>
            )}
          </div>
          {labelName && (
            <div className="sc-crm-create__label-preview">
              <ColorPicker selectedColor={labelColor} onChange={setLabelColor} />
              <div style={{ marginTop: 6 }}>
                <ContactLabel label={{ name: labelName, color: labelColor }} />
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          className="sc-crm-create__submit"
          disabled={!isValid || isCreating}
        >
          {isCreating ? (
            <>
              <MoonLoader size={14} color="#fff" />
              Criando...
            </>
          ) : (
            "Adicionar ao CRM"
          )}
        </button>
      </form>
    </div>
  );
});

CRMCreateForm.displayName = "CRMCreateForm";

export default CRMCreateForm;
